/**
 * Backoff + circuit-breaker around the SPA /renew loop.
 *
 * Why: a single stuck tab can otherwise hammer /renew indefinitely when the
 * axios response interceptor retries on 401. See EAI-5515 postmortem.
 *
 * - >3 consecutive failures: exponential backoff, capped at 5 minutes.
 * - >10 minutes of failures: circuit opens; caller must redirect to sign-in
 *   and stop auto-retries until the user re-authenticates.
 */

const BACKOFF_AFTER_FAILURES = 3;
const BACKOFF_BASE_MS = 30_000;
const BACKOFF_MAX_MS = 5 * 60_000;
const CIRCUIT_OPEN_AFTER_MS = 10 * 60_000;

export class RenewCircuitBreaker {
    private consecutiveFailures = 0;
    private firstFailureAt: number | null = null;
    private nextAttemptAllowedAt = 0;
    private circuitOpen = false;

    canAttempt(): boolean {
        if (this.circuitOpen) return false;
        return Date.now() >= this.nextAttemptAllowedAt;
    }

    isCircuitOpen(): boolean {
        return this.circuitOpen;
    }

    recordSuccess(): void {
        this.consecutiveFailures = 0;
        this.firstFailureAt = null;
        this.nextAttemptAllowedAt = 0;
        this.circuitOpen = false;
    }

    reset(): void {
        this.recordSuccess();
    }

    recordFailure(): { circuitOpened: boolean } {
        const now = Date.now();
        const wasOpen = this.circuitOpen;

        if (this.firstFailureAt === null) {
            this.firstFailureAt = now;
        }
        this.consecutiveFailures++;

        if (now - this.firstFailureAt >= CIRCUIT_OPEN_AFTER_MS) {
            this.circuitOpen = true;
            return { circuitOpened: !wasOpen };
        }

        if (this.consecutiveFailures > BACKOFF_AFTER_FAILURES) {
            const exponent = this.consecutiveFailures - BACKOFF_AFTER_FAILURES - 1;
            const delay = Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * Math.pow(2, exponent));
            this.nextAttemptAllowedAt = now + delay;
        }

        return { circuitOpened: false };
    }
}

export const renewCircuitBreaker = new RenewCircuitBreaker();
